import Pagination from "@/components/Pagination";
import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import Modal from "@/components/Modal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { InlineError } from "@/components/InlineError";
import CategoryImport from "./CategoryImport";
import { Tags, Plus, Pencil, Trash2, Ruler, Loader2, Upload } from "lucide-react";

const empty = { name: "", base_unit: "unité", units: [] };

export default function Categories() {
  const { current } = useActivity();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [confirmSave, setConfirmSave] = useState(false);
  const [delLoading, setDelLoading] = useState(false);
  const [delErr, setDelErr] = useState("");
  const [showImport, setShowImport] = useState(false);

  const [page, setPage] = useState(1);
  const load = async () => { if (!current) return; const { data } = await api.get(`/categories?activity_id=${current.id}`); setRows(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [current]);

  const openNew = () => { setSaveErr(""); setEdit(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setSaveErr(""); setEdit(c); setForm({ name: c.name, base_unit: c.base_unit, units: c.units || [] }); setOpen(true); };

  const addUnit = () => setForm({ ...form, units: [...form.units, { name: "", factor: 1 }] });
  const setUnit = (i, k, v) => setForm({ ...form, units: form.units.map((u, idx) => idx === i ? { ...u, [k]: v } : u) });
  const rmUnit = (i) => setForm({ ...form, units: form.units.filter((_, idx) => idx !== i) });

  const askSave = () => {
    setSaveErr("");
    if (!form.name.trim()) { setSaveErr("Le nom de la catégorie est obligatoire."); return; }
    setConfirmSave(true);
  };
  const save = async () => {
    setSaving(true); setSaveErr("");
    const payload = { activity_id: current.id, name: form.name, base_unit: form.base_unit, units: form.units.map((u) => ({ name: u.name, factor: Number(u.factor || 1) })) };
    try {
      if (edit) await api.patch(`/categories/${edit.id}`, payload); else await api.post("/categories", payload);
      toast.success(edit ? "Catégorie modifiée" : "Catégorie créée"); setConfirmSave(false); setOpen(false); load();
    } catch (e) { setConfirmSave(false); setSaveErr(apiErr(e.response?.data?.detail, e)); }
    finally { setSaving(false); }
  };

  const remove = (c) => { setDelErr(""); setConfirm({
    message: `Supprimer la catégorie « ${c.name} » ? (Impossible si des produits l'utilisent encore.)`,
    action: async () => {
      setDelLoading(true); setDelErr("");
      try { await api.delete(`/categories/${c.id}`); toast.success("Catégorie supprimée"); setConfirm(null); load(); }
      catch (e) { setDelErr(apiErr(e.response?.data?.detail, e)); }
      finally { setDelLoading(false); }
    },
  }); };

  if (!current) return <div className="text-[#64748B]">Sélectionnez une activité.</div>;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Catégories de produits</h1>
          <p className="text-sm text-[#64748B] mt-1">{rows.length} catégorie(s) · {current.name} · chaque catégorie porte ses unités de mesure</p></div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setShowImport(true)} className="btn-outline" data-testid="import-categories-btn"><Upload className="w-4 h-4" /> Importer</button>
          <button onClick={openNew} className="btn-primary" data-testid="new-category-btn"><Plus className="w-4 h-4" /> Nouvelle catégorie</button>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full"><thead><tr>
          <th className="th">Catégorie</th><th className="th">Unité de base</th><th className="th">Conditionnements</th><th className="th text-right">Action</th>
        </tr></thead><tbody>
          {rows.slice((page - 1) * 10, page * 10).map((c) => (
            <tr key={c.id} className="row" data-testid={`category-row-${c.id}`}>
              <td className="td font-medium text-[#0F172A]">{c.name}</td>
              <td className="td"><span className="inline-flex items-center gap-1 text-[#0E7490]"><Ruler className="w-3.5 h-3.5" />{c.base_unit}</span></td>
              <td className="td text-[#64748B] text-xs">{(c.units || []).length ? c.units.map((u) => `${u.name} ×${u.factor}`).join(" · ") : "—"}</td>
              <td className="td text-right whitespace-nowrap">
                <button onClick={() => openEdit(c)} className="btn-edit !py-1.5 !px-3 mr-2" data-testid={`edit-category-${c.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(c)} className="btn-remove !py-1.5 !px-3" data-testid={`delete-category-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="td text-center py-10 text-[#94A3B8]">Aucune catégorie. Créez-en une (ex. Boissons, Ciment, Sable…).</td></tr>}
        </tbody></table>
        <Pagination page={page} setPage={setPage} total={rows.length} />
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Modifier la catégorie" : "Nouvelle catégorie"} testId="category-modal"
        footer={<><button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button><button className="btn-primary" onClick={askSave} disabled={saving} data-testid="save-category-btn">Enregistrer</button></>}>
        <div className="space-y-4">
          <div><label className="lbl">Nom de la catégorie</label><input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name-input" placeholder="Ex : Ciment, Boissons, Sable…" /></div>
          <div><label className="lbl">Unité de base</label><input className="inp" value={form.base_unit} onChange={(e) => setForm({ ...form, base_unit: e.target.value })} data-testid="category-baseunit-input" placeholder="Ex : sac, m³, unité, kg…" /></div>
          <div>
            <div className="flex items-center justify-between mb-2"><label className="lbl !mb-0">Conditionnements (unités de vente groupées)</label>
              <button onClick={addUnit} className="btn-ghost !py-1 text-xs text-[#2563EB]" data-testid="add-unit-btn"><Plus className="w-3.5 h-3.5" /> Ajouter</button></div>
            <div className="space-y-2">
              {form.units.map((u, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input className="inp col-span-6 !py-1.5" placeholder="Nom (ex. palette, casier)" value={u.name} onChange={(e) => setUnit(i, "name", e.target.value)} />
                  <input type="number" className="inp col-span-5 !py-1.5" placeholder="Facteur (× unité de base)" value={u.factor} onChange={(e) => setUnit(i, "factor", e.target.value)} />
                  <button onClick={() => rmUnit(i)} className="col-span-1 text-[#B91C1C] flex justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {form.units.length === 0 && <p className="text-xs text-[#94A3B8]">Aucun conditionnement. Les produits se vendront à l'unité de base.</p>}
            </div>
          </div>
          <InlineError message={saveErr} title={edit ? "Modification impossible" : "Création impossible"} />
        </div>
      </Modal>
      <ConfirmDialog open={!!confirm} title="Supprimer la catégorie" message={confirm?.message} confirmLabel="Supprimer" loading={delLoading} error={delErr} onConfirm={confirm?.action} onClose={() => setConfirm(null)} />
      <ConfirmDialog open={confirmSave} danger={false} title={edit ? "Confirmer la modification" : "Confirmer la création"} message={edit ? `Enregistrer les modifications de « ${form.name} » ?` : `Créer la catégorie « ${form.name} » ?`} confirmLabel={edit ? "Enregistrer" : "Valider et créer"} loading={saving} onConfirm={save} onClose={() => setConfirmSave(false)} />
      <CategoryImport open={showImport} onClose={() => setShowImport(false)} onDone={load} />
    </div>
  );
}
